import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getInsightCatalog } from '../api/modulesApi';
import { errorMessage } from '../api/securityApi';
import { hasPermission } from '../auth/permissions';
import ModuleNav from '../components/Modules/ModuleNav';
import '../components/Modules/modules.css';

// Management pages that are not read-only insights.
const PAGES = [
  { to: '/security', title: 'Security Center', desc: 'Overview, threats, block lists, protection settings.', module: '1-12', perm: 'ivt.read', group: 'Security' },
  { to: '/security/alerts', title: 'Alerts', desc: 'Alert rules and inbox.', module: 13, perm: 'alerts.read', group: 'Security' },
  { to: '/notifications', title: 'Notifications', desc: 'E-mail, Slack, Teams, Discord, webhook channels, digests, quiet hours.', module: '14-18', perm: 'workspace.read', group: 'Notifications' },
  { to: '/incidents', title: 'Incidents & evidence', desc: 'Track incidents, build evidence packages, draft an AdSense appeal.', module: '44-46', perm: 'workspace.read', group: 'Response' },
  { to: '/goals', title: 'Goals, funnels & segments', desc: 'Define conversions and funnels used by the audience reports.', module: '57, 60', perm: 'workspace.read', group: 'Audience' },
  { to: '/adsense', title: 'AdSense data & compliance', desc: 'Import earnings, connect Google, scan pages for policy problems.', module: '63, 69', perm: 'workspace.read', group: 'AdSense' },
  { to: '/reports', title: 'Reports', desc: 'Build, template, schedule, e-mail and share reports.', module: '80-83, 85', perm: 'workspace.read', group: 'Reporting' },
  { to: '/my-dashboards', title: 'My dashboards', desc: 'Compose your own dashboard from any report widget.', module: 84, perm: 'workspace.read', group: 'Reporting' },
  { to: '/work', title: 'Tasks, help & support', desc: 'To-dos, help centre and support tickets.', module: '96-97', perm: 'workspace.read', group: 'Platform' },
  { to: '/account', title: 'Account & security', desc: 'Two-factor sign-in, API keys, webhooks, language, theme and branding.', module: '87-92', perm: null, group: 'Platform' },
  { to: '/admin/audit', title: 'Audit log', desc: 'Who changed what, with CSV export.', module: 86, perm: 'audit.read', group: 'Platform' },
  { to: '/admin', title: 'Admin tools', desc: 'Retention, plans, privacy/GDPR, ML models, diagnostics.', module: '73-100', perm: 'admin.manage', group: 'Platform' },
  { to: '/admin/diagnostics', title: 'Setup diagnostics', desc: 'Checks that everything is wired up and tells you how to fix what is not.', module: 99, perm: 'ivt.read', group: 'Platform' },
];

export default function ModulesHubPage() {
  const [catalog, setCatalog] = useState([]);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => { getInsightCatalog().then(setCatalog).catch((e) => setError(errorMessage(e))); }, []);

  const groups = useMemo(() => {
    const t = q.trim().toLowerCase();
    const match = (s) => !t || s.toLowerCase().includes(t);
    const map = new Map();
    const add = (g, tile) => { if (!map.has(g)) map.set(g, []); map.get(g).push(tile); };
    PAGES.filter((p) => (!p.perm || hasPermission(p.perm)) && match(p.title + p.desc)).forEach((p) => add(p.group, { ...p, key: p.to, link: p.to }));
    catalog.filter((c) => match(c.title + c.description + c.group)).forEach((c) =>
      add(c.group, { key: c.key, title: c.title, desc: c.description, module: c.module, link: `/insights/${c.key}` }));
    return [...map.entries()];
  }, [catalog, q]);

  return (
    <div className="shell">
      <header className="page-header"><div><h1>All modules</h1>
        <p className="muted">Every report and tool you have access to, grouped by area.</p></div></header>
      <ModuleNav />
      {error && <div className="toast error">{error}</div>}
      <div className="sec-toolbar">
        <input className="sec-input" style={{ minWidth: 260 }} placeholder="Search modules…" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="muted">{catalog.length} reports + {PAGES.filter((p) => !p.perm || hasPermission(p.perm)).length} tools</span>
      </div>
      {groups.map(([g, tiles]) => (
        <section key={g}>
          <h2 className="mod-group">{g}</h2>
          <div className="mod-grid">
            {tiles.map((t) => (
              <Link key={t.key} to={t.link} className="mod-tile">
                <span className="mod-no">M{t.module}</span>
                <b>{t.title}</b>
                <small>{t.desc}</small>
              </Link>
            ))}
          </div>
        </section>
      ))}
      {groups.length === 0 && !error && <p className="muted">Nothing matches. If you expected more modules, ask an administrator to grant your role the new "workspace" and analytics permissions.</p>}
    </div>
  );
}
